import type { Static } from "@sinclair/typebox";
import type {
  RouteById,
  SuccessOf,
  VideoCardType,
  VideoDetailType,
  VideoSeriesType,
  VideoState,
} from "@volgo/core/contract";
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { useAcks } from "../policy/ack";
import { useSession } from "../session";
import { Cover, coverOf } from "../ui/GalleryCard";
import { GalleryLink } from "../ui/GalleryLink";
import { Icon } from "../ui/icons";
import { PanelItem } from "../ui/Sidebar";

export const time = (ms: number | null) => {
  const s = Math.floor((ms ?? 0) / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};
export const bytes = (n: number) => `${(n / 1024 ** 3).toFixed(2)} GiB`;
export const pending = (s?: Static<typeof VideoState>) =>
  !!s && ["uploading", "queued", "processing"].includes(s.status);
export const posterOf = (v: VideoCardType) => (v.policy.level !== "block" && "poster" in v ? v.poster : null);
export const visualOf = (v: VideoSeriesType) =>
  v.policy.level !== "block" && "keyVisual" in v ? v.keyVisual : null;
export function useVisiblePoll(active: boolean, run: () => Promise<unknown>) {
  const current = useRef(run);
  current.current = run;
  useEffect(() => {
    if (!active) return;
    let alive = true,
      busy = false;
    const tick = async () => {
      if (!alive || busy || document.hidden) return;
      busy = true;
      try {
        await current.current();
      } catch {
        /* The screen keeps its last known state; the next visible tick retries. */
      } finally {
        busy = false;
      }
    };
    const timer = setInterval(tick, 2000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      alive = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [active]);
}
export function VideoStateNote({ state, name }: { state: Static<typeof VideoState>; name?: string }) {
  const labels = {
    uploading: "올리는 중",
    queued: "처리 대기",
    probing: "파일 확인 중",
    packaging: "HLS로 나누는 중",
    storing: "저장 중",
    published: "공개됨",
    failed: "실패",
    removed: "내려짐",
  };
  const percent =
    state.total && state.done !== null ? Math.min(100, Math.round((state.done / state.total) * 100)) : null;
  return (
    <div className="video-state">
      <div className="video-state-message" role="status" aria-label={name ? `${name} 처리 상태` : undefined}>
        <strong>{labels[state.stage ?? (state.status === "processing" ? "probing" : state.status)]}</strong>
        {state.queuePosition && <span>{state.queuePosition}번째</span>}
        {state.errorMessage && <p>{state.errorMessage}</p>}
        {state.removedReason && <p>{state.removedReason}</p>}
      </div>
      {pending(state) && (
        <progress
          max={100}
          {...(percent === null ? {} : { value: percent })}
          aria-label={name ? `${name} 처리 진행률` : "처리 진행률"}
        />
      )}
      {percent !== null && pending(state) && <span>{percent}%</span>}
    </div>
  );
}
export function VideoCard({
  video,
  children,
  rank,
}: {
  video: VideoCardType | VideoDetailType;
  children?: ReactNode;
  rank?: number;
}) {
  const acks = useAcks();
  const poster = posterOf(video);
  const warned = video.policy.level === "warn" && !acks.isConfirmed(video.id);
  const progress = "progress" in video ? video.progress : null;
  return (
    <article className="video-card" data-video={video.id} data-policy={video.policy.level}>
      {rank && <b className="video-rank">{rank}</b>}
      <Link to={`/videos/${video.id}`} className="video-card-link">
        <div className={`video-cover ${poster ? "" : "hatch"}`}>
          {poster && <Cover src={poster} veiled={warned} />}
          <span className="video-duration">{time(video.durationMs)}</span>
          {video.policy.level !== "none" && (
            <span className="video-policy">
              {video.policy.level === "block" ? "차단된 태그가 포함되어 있습니다" : "경고 태그"}
            </span>
          )}
          {"state" in video && video.state && pending(video.state) && <VideoStateNote state={video.state} />}
          {progress && (
            <progress value={progress.positionMs} max={video.durationMs ?? 1} aria-label="시청 진행률" />
          )}
        </div>
        <h3>{video.title}</h3>
      </Link>
      <p>
        {video.uploader.name} · 조회 {video.viewCount.toLocaleString()}
        {video.height ? ` · ${video.height}p` : ""}
      </p>
      {progress && (
        <small>
          {progress.completed
            ? "다 봄"
            : `${Math.round((progress.positionMs / (video.durationMs || 1)) * 100)}% 봄`}
        </small>
      )}
      {video.policy.level !== "block" &&
        "links" in video &&
        video.links.slice(0, 1).map((g) => (
          <div className="video-source" data-gid={g.gid} key={g.gid}>
            <GalleryLink gid={g.gid}>
              {coverOf(g) && g.policy.level !== "block" && (
                <Cover src={coverOf(g)!} veiled={g.policy.level === "warn"} />
              )}
              <span>원작 · {g.title}</span>
            </GalleryLink>
          </div>
        ))}
      {children}
    </article>
  );
}
export function SeriesCard({ series }: { series: VideoSeriesType }) {
  const src = visualOf(series);
  return (
    <article className="video-card series-card" data-policy={series.policy.level}>
      <Link to={`/videos/s/${series.id}`}>
        <div className={`video-cover ${src ? "" : "hatch"}`}>
          {src && <Cover src={src} veiled={series.policy.level === "warn"} />}
          {series.policy.level !== "none" && (
            <span className="video-policy">
              {series.policy.level === "block" ? "차단된 태그가 포함되어 있습니다" : "경고 태그"}
            </span>
          )}
        </div>
        <h3>{series.title}</h3>
      </Link>
      <p>
        {series.episodeCount}편 · {Math.round(series.totalDurationMs / 60000)}분
      </p>
    </article>
  );
}
export function VideoRow({ title, children, href }: { title: string; children: ReactNode; href?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <section className="video-row" aria-label={title}>
      <div className="sh">
        <h2>{title}</h2>
        <span className="video-row-arrows">
          <button
            type="button"
            aria-label={`${title} 이전`}
            onClick={() =>
              ref.current?.scrollBy({
                left: -700,
                behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
              })
            }
          >
            ‹
          </button>
          <button
            type="button"
            aria-label={`${title} 다음`}
            onClick={() =>
              ref.current?.scrollBy({
                left: 700,
                behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
              })
            }
          >
            ›
          </button>
        </span>
        {href && <Link to={href}>모두</Link>}
      </div>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: arrows move focus between the child card links */}
      <div
        className="video-rail"
        ref={ref}
        onKeyDown={(e) => {
          if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
          const links = [...e.currentTarget.querySelectorAll<HTMLAnchorElement>(".video-card > a")];
          const at = links.indexOf(document.activeElement as HTMLAnchorElement);
          const next = links[at + (e.key === "ArrowRight" ? 1 : -1)];
          if (next) {
            e.preventDefault();
            next.focus();
          }
        }}
      >
        {children}
      </div>
    </section>
  );
}
type Mine = SuccessOf<RouteById<"videos.mine">>;
export const VideoSummaryContext = createContext<{ mine: Mine | null; refresh: () => void }>({
  mine: null,
  refresh() {},
});
export function useVideoSummaryState() {
  const { client } = useSession();
  const [mine, setMine] = useState<Mine | null>(null);
  const [revision, setRevision] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: revision reloads after a local upload mutation
  useEffect(() => {
    let live = true;
    client.call("videos.mine", { query: { limit: 1 } }).then(
      (v) => live && setMine(v),
      () => {},
    );
    return () => {
      live = false;
    };
  }, [client, revision]);
  useVisiblePoll(!!mine?.processing.count, async () =>
    setMine(await client.call("videos.mine", { query: { limit: 1 } })),
  );
  useEffect(() => {
    const update = () => setRevision((r) => r + 1);
    window.addEventListener("volgo:videos", update);
    return () => window.removeEventListener("volgo:videos", update);
  }, []);
  return { mine, refresh: () => setRevision((r) => r + 1) };
}
export const videoChanged = () => window.dispatchEvent(new Event("volgo:videos"));
/** "올린 영상" and "저장한 영상" in the panel's 내 보관함, shaped like the downloads row (48 Spec 3.1). */
export function VideoLibrary() {
  const { mine } = useContext(VideoSummaryContext);
  const { pathname } = useLocation();
  const processing = mine?.processing;
  const percent =
    processing?.done != null && processing.total
      ? Math.round((processing.done / processing.total) * 100)
      : null;
  return (
    <>
      <PanelItem
        to="/videos/mine"
        icon={<Icon name="upload" />}
        label="올린 영상"
        current={pathname === "/videos/mine"}
      >
        {!!processing?.count && <span className="n">{processing.count}개 처리 중</span>}
      </PanelItem>
      {!!processing?.count && (
        <div className="dlmini">
          <p>
            <span>처리 진행</span>
            {percent !== null && <span>{percent}%</span>}
          </p>
          <div className="prog" data-pending={percent === null ? "true" : undefined}>
            {percent !== null && <i style={{ width: `${percent}%` }} />}
          </div>
        </div>
      )}
      <PanelItem
        to="/videos/saved"
        icon={<Icon name="lib" />}
        label="저장한 영상"
        current={pathname === "/videos/saved"}
      />
    </>
  );
}
export function GalleryVideos({ gid }: { gid: number }) {
  const { client } = useSession();
  const [items, setItems] = useState<VideoCardType[]>([]);
  useEffect(() => {
    let live = true;
    setItems([]);
    client.call("videos.gallery", { params: { gid }, query: { limit: 6 } }).then(
      (v) => live && setItems(v.items),
      () => {},
    );
    return () => {
      live = false;
    };
  }, [client, gid]);
  return items.length ? (
    <VideoRow title="이 작품의 영상">
      {items.map((v) => (
        <VideoCard key={v.id} video={v} />
      ))}
    </VideoRow>
  ) : null;
}
